import React from 'react';
import { DELIVERY_TERM_ORDER, deliveryTermLabel } from '../../lib/supplierDeliveryTerms';

// "Where can you deliver?" — asked ONCE, then never again.
//
// ── Why it lives on the quote form and not in settings ──────────────────────
// A supplier arriving from a public RFQ link has no reason to visit settings;
// they came to quote. A capability question parked behind a settings tab is a
// question that is never answered, and then the prefill it exists to power
// never happens for exactly the suppliers the links recruit.
//
// So it is asked in the place they already are, ONCE (the host renders this
// only while profiles.delivery_terms is empty), and it replaces work rather
// than adding it: answering here pre-picks the price basis on this quote and
// every later one.
//
// ── Multi-select, and the order is not arbitrary ────────────────────────────
// EXW → FOB → CIF → DDP is least-to-most carried by the supplier. A factory
// reads down the list and stops where they stop. Presenting them in any other
// order asks them to hold four unrelated options in mind at once.
export default function DeliveryCapability({
  lang = 'en',
  value = [],              // string[] of selected codes
  onChange,                // (nextArray) => void
  t,                       // { title, hint } from the host's string pack
  styles = {},
}) {
  const isAr = lang === 'ar';
  const label = styles.label || {};
  const selected = Array.isArray(value) ? value : [];

  const toggle = (code) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    // Kept in the canonical order rather than click order, so what is stored
    // does not depend on which box the supplier happened to tick first.
    onChange(DELIVERY_TERM_ORDER.filter((c) => next.includes(c)));
  };

  return (
    <div style={{
      marginBottom: 18, padding: 14, borderRadius: 8,
      background: 'var(--bg-base)', border: '1px solid var(--border-subtle)',
    }}>
      <span style={label}>{t.title}</span>
      <p style={{
        margin: '0 0 10px', fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)',
        fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
      }}>
        {t.hint}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {DELIVERY_TERM_ORDER.map((code) => {
          const copy = deliveryTermLabel(code, lang);
          if (!copy) return null;   // positive lookup: no label, no option
          const on = selected.includes(code);
          return (
            <label key={code} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
              padding: '9px 11px', borderRadius: 8,
              border: '1px solid', borderColor: on ? 'var(--border-muted)' : 'var(--border-subtle)',
              background: on ? 'var(--bg-subtle)' : 'transparent',
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
            }}>
              <input type="checkbox" checked={on} onChange={() => toggle(code)}
                style={{ marginTop: 3, cursor: 'pointer' }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, color: 'var(--text-primary)' }}>
                  {copy.title}
                  <span style={{ color: 'var(--text-disabled)', marginInlineStart: 6, fontSize: 12 }}>{code}</span>
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.6 }}>
                  {copy.hint}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
